import React, { useState } from 'react';
import classNames from 'classnames';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCheckDouble,
  faExclamationTriangle,
  faKey,
  faLock,
  faPaperPlane,
  faSignInAlt,
  faTimesCircle,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import useRequestState from 'hooks/useRequestState';
import {
  NETWORK_ERROR_MESSAGE,
  getApiErrorMessage,
  setInvitePassword
} from 'helpers/registrationApi';
import { INVITE_LINK_TTL_HOURS } from 'helpers/registrationMeta';
import {
  LinkExpiredIllustration,
  LinkInvalidIllustration,
  LinkUsedIllustration,
  SetPasswordHeroIllustration
} from 'components/registration-shared/RegistrationIllustrations';
import { StateMessage } from 'components/registration-shared/RegistrationUI';
import PasswordField from 'components/registration-shared/PasswordField';
import paths from 'routes/paths';
import RegistrationLayout from './registration/RegistrationLayout';
import PasswordStrengthMeter, {
  evaluatePassword
} from './registration/PasswordStrengthMeter';

/**
 * Invite set-password screen — /set-password/:hash (userregistrations.md U5).
 * Modeled on PasswordResetForm.js and, crucially, sends the password exactly
 * the way it does: plain { hash, password, confirm_password } — no
 * client-side hashing — so the stored credential matches at login (K2).
 */

const JOURNEY_STEPS = [
  {
    icon: faPaperPlane,
    title: 'Request sent',
    shortTitle: 'Requested',
    text: 'You asked for access.',
    done: true
  },
  {
    icon: faUserCheck,
    title: 'Request approved',
    shortTitle: 'Approved',
    text: 'Your account is ready.',
    done: true
  },
  {
    icon: faKey,
    title: 'Set your password',
    shortTitle: 'Password',
    text: 'Then sign in.'
  }
];

// Which dead-end is this? Expired / already used / invalid links each get
// their own screen with a way forward (U5.3).
const classifyInviteError = (message = '', httpStatus) => {
  const text = String(message).toLowerCase();
  const aboutLink = /(link|hash|invite|token|invitation)/.test(text);
  if (httpStatus === 410 || /expired/.test(text)) {
    return 'expired';
  }
  if (aboutLink && /(already|been used|used|consumed)/.test(text)) {
    return 'used';
  }
  if (
    httpStatus === 404 ||
    (aboutLink &&
      /(invalid|not found|no longer|does not exist|malformed)/.test(text))
  ) {
    return 'invalid';
  }
  return null;
};

const SetInvitePasswordForm = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const [values, setValues] = useState({ password: '', confirmPassword: '' });
  const [visible, setVisible] = useState({
    password: false,
    confirmPassword: false
  });
  const [touched, setTouched] = useState({});
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [linkState, setLinkState] = useState(hash ? 'ready' : 'invalid');
  const [serverError, setServerError] = useState('');
  const submitReq = useRequestState();

  const { requiredMet } = evaluatePassword(values.password);
  const matches =
    values.confirmPassword.length > 0 &&
    values.password === values.confirmPassword;

  const errors = {
    password: !values.password
      ? 'Enter a password.'
      : !requiredMet
      ? 'Use at least 8 characters, including a letter and a number.'
      : '',
    confirmPassword: !values.confirmPassword
      ? 'Enter your password again.'
      : !matches
      ? "Passwords don't match yet."
      : ''
  };

  const showError = name => (touched[name] ? errors[name] : '');

  const handleChange = e => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    setServerError('');
  };

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleKeyUp = e => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitReq.isLoading) {
      return;
    }
    setTouched({ password: true, confirmPassword: true });
    setServerError('');

    if (errors.password || errors.confirmPassword) {
      document
        .getElementById(errors.password ? 'invite-password' : 'invite-confirm')
        ?.focus();
      return;
    }

    const result = await submitReq.run(() =>
      setInvitePassword({
        hash,
        password: values.password,
        confirmPassword: values.confirmPassword
      })
    );
    if (result.stale) {
      return;
    }

    if (!result.ok) {
      setServerError(getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE));
      return;
    }

    const response = result.data;
    if (response.ok) {
      toast.success('Your password is set. Sign in to get started.', {
        theme: 'colored'
      });
      navigate('/', { replace: true });
      return;
    }

    const deadEnd = classifyInviteError(response.message, response.httpStatus);
    if (deadEnd) {
      setLinkState(deadEnd);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setServerError(response.message);
    }
  };

  let body;

  if (linkState === 'expired') {
    body = (
      <StateMessage
        art={<LinkExpiredIllustration width={200} />}
        title="This link has expired"
        actions={
          <>
            <Link
              to={paths.splitForgotPassword}
              className="btn mym-btn-primary"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              Email me a new link
            </Link>
            <Link to="/" className="btn btn-falcon-default">
              <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
              Back to sign in
            </Link>
          </>
        }
      >
        Set-password links expire after {INVITE_LINK_TTL_HOURS} hours. Your
        account is ready, so request a new link with your email address.
      </StateMessage>
    );
  } else if (linkState === 'used') {
    body = (
      <StateMessage
        art={<LinkUsedIllustration width={200} />}
        title="This link has already been used"
        actions={
          <>
            <Link to="/" className="btn mym-btn-primary">
              <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
              Go to sign in
            </Link>
            <Link
              to={paths.splitForgotPassword}
              className="btn btn-falcon-default"
            >
              <FontAwesomeIcon icon={faKey} className="me-2" />
              Forgot your password?
            </Link>
          </>
        }
      >
        Your password is already set. Sign in, or reset it if you've forgotten
        it.
      </StateMessage>
    );
  } else if (linkState === 'invalid') {
    body = (
      <StateMessage
        art={<LinkInvalidIllustration width={200} />}
        title="This link isn't valid"
        actions={
          <>
            <Link
              to={paths.splitForgotPassword}
              className="btn mym-btn-primary"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
              Request a new link
            </Link>
            <Link to="/" className="btn btn-falcon-default">
              <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
              Back to sign in
            </Link>
          </>
        }
      >
        The link may be incomplete. Open it directly from your welcome email, or
        request a new one.
      </StateMessage>
    );
  } else {
    const confirmError = showError('confirmPassword');
    body = (
      <Form noValidate onSubmit={handleSubmit}>
        <div className="mym-reg-header">
          <span className="mym-reg-kicker is-success">
            <FontAwesomeIcon icon={faCheckCircle} />
            Account approved
          </span>
          <h2 className="mym-reg-title">Set your password</h2>
          <p className="mym-reg-lead">
            Use a strong password you don't use anywhere else.
          </p>
        </div>

        <div className="mb-3">
          <PasswordField
            id="invite-password"
            name="password"
            label="New password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyUp={handleKeyUp}
            visible={visible.password}
            onToggle={() =>
              setVisible(prev => ({ ...prev, password: !prev.password }))
            }
            error={showError('password')}
            icon={faLock}
            describedBy="invite-password-strength"
            autoComplete="new-password"
          />
          <PasswordStrengthMeter
            id="invite-password-strength"
            password={values.password}
          />
        </div>

        <div className="mb-2">
          <PasswordField
            id="invite-confirm"
            name="confirmPassword"
            label="Confirm password"
            value={values.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyUp={handleKeyUp}
            visible={visible.confirmPassword}
            onToggle={() =>
              setVisible(prev => ({
                ...prev,
                confirmPassword: !prev.confirmPassword
              }))
            }
            error={confirmError}
            icon={faCheckDouble}
            autoComplete="new-password"
          />
          {!confirmError && values.confirmPassword && (
            <div
              className={classNames(
                'mym-field-hint',
                matches ? 'is-success' : 'is-warning'
              )}
            >
              <FontAwesomeIcon icon={matches ? faCheckCircle : faTimesCircle} />
              {matches ? 'Passwords match' : "Passwords don't match yet"}
            </div>
          )}
        </div>

        {capsLockOn && (
          <div className="mym-field-hint is-warning mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Caps Lock is on
          </div>
        )}

        {serverError && (
          <IconAlert variant="danger" className="mt-3 mb-0">
            <p className="mb-0 fw-semibold">{serverError}</p>
          </IconAlert>
        )}

        <div className="d-grid mt-4">
          <Button
            type="submit"
            className="mym-btn-primary"
            disabled={submitReq.isLoading}
          >
            {submitReq.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              'Set password'
            )}
          </Button>
        </div>

        <p className="text-center fs-10 text-600 mt-3 mb-0">
          Link not working?{' '}
          <Link to={paths.splitForgotPassword}>Request a new one</Link>
        </p>
      </Form>
    );
  }

  return (
    <RegistrationLayout
      title={
        <>
          You're approved.
          <br />
          Set your password.
        </>
      }
      hero={<SetPasswordHeroIllustration />}
      steps={JOURNEY_STEPS}
    >
      {body}
    </RegistrationLayout>
  );
};

export default SetInvitePasswordForm;
